import { currencyFormatter } from '@/helpers/currencyFormatter';
import { useAcknowledgeEnglishOnlyNoticeMutation, useAcknowledgeKeyTermsMutation } from '@/services/contractSigning';
import { PalmettoButton } from '@palmetto/dsm-components';
import { useCallback } from 'react';

import { CongaKeyTerms, Locale } from 'types';

import { Copy, copyText, inlineCopy } from './ContractSigningCopy';

interface AcknowledgementStepProps {
  inviteToken: string;
  locales: Locale[];
}

/** Shown when a Spanish contract falls in a market whose Conga contract is English only. */
export const EnglishOnlyNoticeStep = ({ inviteToken, locales }: AcknowledgementStepProps) => {
  const [acknowledge, { isLoading, isError }] = useAcknowledgeEnglishOnlyNoticeMutation();
  const handleAcknowledge = useCallback(() => {
    void acknowledge({ inviteToken });
  }, [acknowledge, inviteToken]);

  return (
    <div className="flex flex-col gap-6">
      <Copy copyKey="englishOnlyHeading" locales={locales} variant="heading" />
      <Copy copyKey="englishOnlyBody" locales={locales} />
      <PalmettoButton type="button" emphasis="primary" size="large" loading={isLoading} onClick={handleAcknowledge}>
        {inlineCopy(locales, 'englishOnlyAcknowledge')}
      </PalmettoButton>
      {isError && <Copy copyKey="genericError" locales={locales} variant="error" />}
    </div>
  );
};

const KeyTermRow = ({ label, value }: { label: string; value: string }) => (
  <div className="gap-1 py-3 flex flex-col border-t border-canvas-inverted">
    <span className="body-200 text-text-secondary">{label}</span>
    <span className="heading-300 text-text-primary">{value || '—'}</span>
  </div>
);

// The contract payload carries the payment as a bare decimal; Conga adds the currency symbol in the document.
const formatPayment = (value: string) =>
  value.trim() !== '' && Number.isFinite(Number(value)) ? currencyFormatter.format(Number(value)) : value;

/** Values are shown exactly as they go into the contract, so the page can never disagree with it. */
export const KeyTermsStep = ({
  inviteToken,
  locales,
  keyTerms,
}: AcknowledgementStepProps & { keyTerms: CongaKeyTerms }) => {
  const [acknowledge, { isLoading, isError }] = useAcknowledgeKeyTermsMutation();
  const handleAcknowledge = useCallback(() => {
    void acknowledge({ inviteToken });
  }, [acknowledge, inviteToken]);

  const years = keyTerms.agreementTermYears;

  return (
    <div className="flex flex-col gap-6">
      <Copy copyKey="keyTermsHeading" locales={locales} variant="heading" />
      <div className="flex flex-col">
        <KeyTermRow
          label={inlineCopy(locales, 'keyTermsYearOnePayment')}
          value={formatPayment(keyTerms.yearOneMonthlyPayment)}
        />
        <KeyTermRow label={inlineCopy(locales, 'keyTermsEscalator')} value={keyTerms.annualEscalatorRate} />
        <KeyTermRow
          label={inlineCopy(locales, 'keyTermsTerm')}
          value={years === null ? '' : copyText(locales[0], 'keyTermsYears', { years })}
        />
      </div>
      <Copy copyKey="keyTermsHelp" locales={locales} />
      <PalmettoButton type="button" emphasis="primary" size="large" loading={isLoading} onClick={handleAcknowledge}>
        {inlineCopy(locales, 'keyTermsAcknowledge')}
      </PalmettoButton>
      {isError && <Copy copyKey="genericError" locales={locales} variant="error" />}
    </div>
  );
};
